import React, { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../layouts/PageTitle";
import { Formik } from "formik";
import * as Yup from "yup";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";

const FirmSchema = Yup.object().shape({
  Name: Yup.string().required("Firm name is required"),
  F_FirmType: Yup.string().required("Firm type is required"),
});

const emptyBranchRow = () => ({
  Id: 0,
  Name: "",
  CoAddress: "",
  OfficeAddress: "",
  PhoneMobileNo: "",
  IsMainBranch: 0,
  PFNo: "",
  ESINo: "",
  LINNo: "",
  FBRegNo: "",
  RegRenDate: "",
  RegExpiryDate: "",
  PFUserName: "",
  PFPassword: "",
  ESIUserName: "",
  ESIPassword: "",
  FactoryActUserName: "",
  FactoryActPassword: "",
  EmploymentUserName: "",
  EmploymentPassword: "",
  LabourUserName: "",
  LabourPassword: "",
});

const AddEdit_FirmMaster = () => {
  const [activeTab, setActiveTab] = useState("basicInfo");
  const [branchRows, setBranchRows] = useState([emptyBranchRow()]);
  const [state, setState] = useState({
    id: 0,
    formData: {
      Name: "",
      F_FirmType: "",
      NatureOfBusiness: "",
      Signature: "",
      MaxWorkers: "",
      UseOfPower: "",
      F_ConcernType: "",
      F_PremisesType: "",
      LicenceFee: "",
      DifferenceFee: "",
      AmendmentFee: "",
      DuplicateLicenceFee: "",
      InTime: "",
      OutTime: "",
      CentralRuleMinWages: false,
      Remark: "",
    },
    FirmType: [],
    ConcernType: [],
    PremisesType: [],
    isProgress: true,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const API_URL_SAVE = "FirmMaster/0/token";
  const API_URL_EDIT = `${API_WEB_URLS.MASTER}/0/token/FirmMaster/Id`;

  useEffect(() => {
    Fn_FillListData(dispatch, setState, "FirmType", `${API_WEB_URLS.MASTER}/0/token/FirmTypeMaster/Id/0`);
    Fn_FillListData(dispatch, setState, "ConcernType", `${API_WEB_URLS.MASTER}/0/token/ConcernTypeMaster/Id/0`);
    Fn_FillListData(dispatch, setState, "PremisesType", `${API_WEB_URLS.MASTER}/0/token/PremisesTypeMaster/Id/0`);

    const Id = (location.state && location.state.Id) || 0;
    if (Id > 0) {
      setState((prev) => ({ ...prev, id: Id }));
      const fetchData = async () => {
        const res = await Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
        console.log("Fetched FirmMaster data:", res.dataList[0]);
      };
      fetchData();
    }
  }, [dispatch, location.state]);

  // Populate branch rows when editing
  useEffect(() => {
    if (state.formData && state.formData.BranchData) {
      try {
        const parsed = JSON.parse(state.formData.BranchData);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setBranchRows(parsed.map((b) => ({ ...b, IsMainBranch: b.IsMainBranch ? 1 : 0 })));
        }
      } catch (_) {}
    }
  }, [state.formData]);

  // Branch table handlers
  const addBranchRow = () => setBranchRows((prev) => [...prev, emptyBranchRow()]);

  const removeBranchRow = (index) => {
    if (branchRows.length === 1) return;
    setBranchRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBranchChange = (index, field, value) => {
    setBranchRows((prev) =>
      prev.map((row, i) => {
        if (field === "IsMainBranch") {
          // Only the clicked row gets IsMainBranch = 1; all others reset to 0
          return { ...row, IsMainBranch: i === index && value ? 1 : 0 };
        }
        return i === index ? { ...row, [field]: value } : row;
      })
    );
  };

  const handleSubmit = async (values) => {
    try {
      const formData = new FormData();
      formData.append("Name", values.Name);
      formData.append("F_FirmType", values.F_FirmType);
      formData.append("NatureOfBusiness", values.NatureOfBusiness || "");
      formData.append("Signature", values.Signature || "");
      formData.append("MaxWorkers", values.MaxWorkers || 0);
      formData.append("UseOfPower", values.UseOfPower || "");
      formData.append("F_ConcernType", values.F_ConcernType || 0);
      formData.append("F_PremisesType", values.F_PremisesType || 0);
      formData.append("LicenceFee", values.LicenceFee || 0);
      formData.append("DifferenceFee", values.DifferenceFee || 0);
      formData.append("AmendmentFee", values.AmendmentFee || 0);
      formData.append("DuplicateLicenceFee", values.DuplicateLicenceFee || 0);
      formData.append("InTime", values.InTime || "");
      formData.append("OutTime", values.OutTime || "");
      formData.append("CentralRuleMinWages", values.CentralRuleMinWages ? true : false);
      formData.append("Remark", values.Remark || "");
      formData.append("BranchJson", JSON.stringify(branchRows));

      Fn_AddEditData(
        dispatch,
        setState,
        { arguList: { id: state.id, formData } },
        API_URL_SAVE,
        true,
        "memberid",
        navigate,
        "/FirmMaster"
      );
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred while submitting the form. Please try again.");
    }
  };

  const tabs = [
    { key: "basicInfo", label: "Basic Info" },
    { key: "licenseFees", label: "License Fees" },
    { key: "branches", label: "Branches" },
  ];

  return (
    <Fragment>
      <PageTitle activeMenu="Masters" motherMenu="Masters" pageContent="Firm Master" />

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Firm Master</h4>
            </div>
            <div className="card-body">
              {/* Tabs Nav */}
              <ul className="nav nav-tabs mb-4" role="tablist">
                {tabs.map((tab) => (
                  <li className="nav-item" key={tab.key}>
                    <button
                      className={`nav-link${activeTab === tab.key ? " active" : ""}`}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>

              <Formik
                initialValues={state.formData}
                enableReinitialize
                validationSchema={FirmSchema}
                onSubmit={(values, { setSubmitting }) => {
                  setTimeout(() => {
                    handleSubmit(values);
                    setSubmitting(false);
                  }, 400);
                }}
              >
                {({ values, errors, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => (
                  <form onSubmit={handleSubmit}>

                    {/* ── Tab 1: Basic Info ── */}
                    {activeTab === "basicInfo" && (
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="text-label">Firm Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            name="Name"
                            value={values.Name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.Name && <div className="text-danger fs-12">{errors.Name}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Firm Type *</label>
                          <select
                            className="form-control"
                            name="F_FirmType"
                            value={values.F_FirmType}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">-- Select Firm Type --</option>
                            {state.FirmType &&
                              state.FirmType.map((item) => (
                                <option key={item.Id} value={item.Id}>
                                  {item.Name}
                                </option>
                              ))}
                          </select>
                          {errors.F_FirmType && <div className="text-danger fs-12">{errors.F_FirmType}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Concern Type</label>
                          <select
                            className="form-control"
                            name="F_ConcernType"
                            value={values.F_ConcernType}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">-- Select Concern Type --</option>
                            {state.ConcernType &&
                              state.ConcernType.map((item) => (
                                <option key={item.Id} value={item.Id}>
                                  {item.Name}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Premises Type</label>
                          <select
                            className="form-control"
                            name="F_PremisesType"
                            value={values.F_PremisesType}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">-- Select Premises Type --</option>
                            {state.PremisesType &&
                              state.PremisesType.map((item) => (
                                <option key={item.Id} value={item.Id}>
                                  {item.Name}
                                </option>
                              ))}
                          </select>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Nature of Business</label>
                          <input
                            type="text"
                            className="form-control"
                            name="NatureOfBusiness"
                            value={values.NatureOfBusiness}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>

                        <div className="col-md-3 mb-3">
                          <label className="text-label">Max Workers</label>
                          <input
                            type="number"
                            className="form-control"
                            name="MaxWorkers"
                            value={values.MaxWorkers}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>

                        <div className="col-md-3 mb-3">
                          <label className="text-label">Use of Power</label>
                          <input
                            type="text"
                            className="form-control"
                            name="UseOfPower"
                            value={values.UseOfPower}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>

                        <div className="col-md-3 mb-3">
                          <label className="text-label">In Time</label>
                          <input
                            type="time"
                            className="form-control"
                            name="InTime"
                            value={values.InTime}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>

                        <div className="col-md-3 mb-3">
                          <label className="text-label">Out Time</label>
                          <input
                            type="time"
                            className="form-control"
                            name="OutTime"
                            value={values.OutTime}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Signature</label>
                          <input
                            type="text"
                            className="form-control"
                            name="Signature"
                            value={values.Signature}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>

                        <div className="col-md-6 mb-3 d-flex align-items-center mt-4">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="CentralRuleMinWages"
                              name="CentralRuleMinWages"
                              checked={!!values.CentralRuleMinWages}
                              onChange={(e) => setFieldValue("CentralRuleMinWages", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="CentralRuleMinWages">
                              Central Rule Min Wages
                            </label>
                          </div>
                        </div>

                        <div className="col-md-12 mb-3">
                          <label className="text-label">Remark</label>
                          <textarea
                            className="form-control"
                            name="Remark"
                            rows={3}
                            value={values.Remark}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>
                      </div>
                    )}

                    {/* ── Tab 2: License Fees ── */}
                    {activeTab === "licenseFees" && (
                      <div className="row">
                        <div className="col-md-3 mb-3">
                          <label className="text-label">Licence Fee</label>
                          <input type="number" step="0.01" className="form-control" name="LicenceFee" value={values.LicenceFee} onChange={handleChange} onBlur={handleBlur} />
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="text-label">Difference Fee</label>
                          <input type="number" step="0.01" className="form-control" name="DifferenceFee" value={values.DifferenceFee} onChange={handleChange} onBlur={handleBlur} />
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="text-label">Amendment Fee</label>
                          <input type="number" step="0.01" className="form-control" name="AmendmentFee" value={values.AmendmentFee} onChange={handleChange} onBlur={handleBlur} />
                        </div>
                        <div className="col-md-3 mb-3">
                          <label className="text-label">Duplicate Licence Fee</label>
                          <input type="number" step="0.01" className="form-control" name="DuplicateLicenceFee" value={values.DuplicateLicenceFee} onChange={handleChange} onBlur={handleBlur} />
                        </div>
                      </div>
                    )}

                    {/* ── Tab 5: Branches ── */}
                    {activeTab === "branches" && (
                      <div>
                        <div className="d-flex justify-content-end mb-3">
                          <button type="button" className="btn btn-success btn-sm" onClick={addBranchRow}>
                            <i className="fa fa-plus me-1"></i>Add Branch
                          </button>
                        </div>
                        {branchRows.map((row, index) => (
                          <div key={index} className="card border mb-3">
                            <div className="card-header d-flex justify-content-between align-items-center py-2">
                              <strong>Branch {index + 1}{row.Name ? ` — ${row.Name}` : ""}</strong>
                              <button
                                type="button"
                                className="btn btn-danger btn-xs"
                                onClick={() => removeBranchRow(index)}
                                disabled={branchRows.length === 1}
                              >
                                <i className="fa fa-minus"></i>
                              </button>
                            </div>
                            <div className="card-body">
                              {/* Basic */}
                              <h6 className="text-primary border-bottom pb-1 mb-3">Basic Info</h6>
                              <div className="row">
                                <div className="col-md-4 mb-3">
                                  <label className="text-label">Branch Name</label>
                                  <input type="text" className="form-control" value={row.Name} onChange={(e) => handleBranchChange(index, "Name", e.target.value)} />
                                </div>
                                <div className="col-md-4 mb-3">
                                  <label className="text-label">C/O Address</label>
                                  <input type="text" className="form-control" value={row.CoAddress} onChange={(e) => handleBranchChange(index, "CoAddress", e.target.value)} />
                                </div>
                                <div className="col-md-4 mb-3">
                                  <label className="text-label">Office Address</label>
                                  <input type="text" className="form-control" value={row.OfficeAddress} onChange={(e) => handleBranchChange(index, "OfficeAddress", e.target.value)} />
                                </div>
                                <div className="col-md-4 mb-3">
                                  <label className="text-label">Phone / Mobile No</label>
                                  <input type="text" className="form-control" value={row.PhoneMobileNo} onChange={(e) => handleBranchChange(index, "PhoneMobileNo", e.target.value)} />
                                </div>
                                <div className="col-md-4 mb-3 d-flex align-items-center mt-2">
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id={`IsMainBranch_${index}`}
                                      checked={row.IsMainBranch === 1}
                                      onChange={(e) => handleBranchChange(index, "IsMainBranch", e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor={`IsMainBranch_${index}`}>Main Branch</label>
                                  </div>
                                </div>
                              </div>

                              {/* Registration */}
                              <h6 className="text-primary border-bottom pb-1 mb-3 mt-2">Registration</h6>
                              <div className="row">
                                <div className="col-md-3 mb-3">
                                  <label className="text-label">PF No</label>
                                  <input type="text" className="form-control" value={row.PFNo} onChange={(e) => handleBranchChange(index, "PFNo", e.target.value)} />
                                </div>
                                <div className="col-md-3 mb-3">
                                  <label className="text-label">ESI No</label>
                                  <input type="text" className="form-control" value={row.ESINo} onChange={(e) => handleBranchChange(index, "ESINo", e.target.value)} />
                                </div>
                                <div className="col-md-3 mb-3">
                                  <label className="text-label">LIN No</label>
                                  <input type="text" className="form-control" value={row.LINNo} onChange={(e) => handleBranchChange(index, "LINNo", e.target.value)} />
                                </div>
                                <div className="col-md-3 mb-3">
                                  <label className="text-label">FB Reg No</label>
                                  <input type="text" className="form-control" value={row.FBRegNo} onChange={(e) => handleBranchChange(index, "FBRegNo", e.target.value)} />
                                </div>
                                <div className="col-md-3 mb-3">
                                  <label className="text-label">Reg / Renewal Date</label>
                                  <input type="date" className="form-control" value={row.RegRenDate ? row.RegRenDate.split("T")[0] : ""} onChange={(e) => handleBranchChange(index, "RegRenDate", e.target.value)} />
                                </div>
                                <div className="col-md-3 mb-3">
                                  <label className="text-label">Reg Expiry Date</label>
                                  <input type="date" className="form-control" value={row.RegExpiryDate ? row.RegExpiryDate.split("T")[0] : ""} onChange={(e) => handleBranchChange(index, "RegExpiryDate", e.target.value)} />
                                </div>
                              </div>

                              {/* Credentials */}
                              <h6 className="text-primary border-bottom pb-1 mb-3 mt-2">Credentials</h6>
                              <div className="row">
                                {[
                                  { label: "PF",           user: "PFUserName",          pass: "PFPassword" },
                                  { label: "ESI",          user: "ESIUserName",         pass: "ESIPassword" },
                                  { label: "Factory Act",  user: "FactoryActUserName",  pass: "FactoryActPassword" },
                                  { label: "Employment",   user: "EmploymentUserName",  pass: "EmploymentPassword" },
                                  { label: "Labour",       user: "LabourUserName",      pass: "LabourPassword" },
                                ].map(({ label, user, pass }) => (
                                  <Fragment key={label}>
                                    <div className="col-12 mb-1"><small className="text-muted fw-semibold">{label}</small></div>
                                    <div className="col-md-3 mb-3">
                                      <label className="text-label">Username</label>
                                      <input type="text" className="form-control" value={row[user]} onChange={(e) => handleBranchChange(index, user, e.target.value)} />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                      <label className="text-label">Password</label>
                                      <input type="text" className="form-control" value={row[pass]} onChange={(e) => handleBranchChange(index, pass, e.target.value)} />
                                    </div>
                                  </Fragment>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ── Submit / Cancel ── */}
                    <div className="mt-4">
                      <button type="submit" className="btn btn-primary me-2" disabled={isSubmitting}>
                        {isSubmitting ? "Saving..." : "Save"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger light"
                        onClick={() => navigate("/FirmMaster")}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddEdit_FirmMaster;
